// Session 58 Sprint A · Owner Setup Checklist
// Shown to team-assigned owners/managers on first login. Reads the derived
// checklist from /api/owner/onboarding-status and lets the user acknowledge
// the manual-only step (preview listing). Autosaves acknowledgments.
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import AppShell from "../components/AppShell";
import { toast, Toaster } from "sonner";

export default function OwnerSetupChecklist() {
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("");
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  // Session 59.5 · P0 · Load venues once. Uses a functional state update
  // for the default-selection guard so we don't need `venueId` as a
  // dependency — refetching on every venue selection would be wasteful.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/owner/venues");
        const list = r.data || [];
        setVenues(list);
        if (list.length > 0) {
          setVenueId((cur) => cur || list[0].location_id || list[0].venue_id);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load venues");
      }
    })();
  }, []);

  const loadStatus = useCallback(async () => {
    if (!venueId) return;
    try {
      const r = await api.get("/owner/onboarding-status", { params: { venue_id: venueId } });
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load checklist");
    }
  }, [venueId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const toggleManual = async (step, acknowledged) => {
    setSaving(true);
    try {
      await api.post("/owner/onboarding-ack", {
        venue_id: venueId, step_key: step.key, acknowledged,
      });
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const venue = venues.find(v => (v.location_id || v.venue_id) === venueId);

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 20px",
                     fontFamily: "Georgia, serif", color: "#1B2340" }}
           data-testid="owner-setup-checklist">
        <h1 style={{ fontSize: 32, fontWeight: 500, margin: "0 0 6px",
                     fontFamily: "Cormorant Garamond, Georgia, serif" }}>
          Welcome to your Métier Workspace Dashboard
        </h1>
        <p style={{ fontSize: 15, color: "rgba(27,35,64,0.75)", lineHeight: 1.6, margin: "0 0 20px", maxWidth: 720 }}>
          Everything you need to manage how your venue appears, when your workspaces are available,
          and how members book with you lives here. Complete the steps below to make sure your listing is ready for members.
        </p>

        {venues.length > 1 && (
          <label style={{ display: "block", marginBottom: 20 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.08em",
                            textTransform: "uppercase", color: "rgba(27,35,64,0.65)" }}>Venue</span>
            <select value={venueId} onChange={(e) => setVenueId(e.target.value)}
                     data-testid="setup-venue-select"
                     style={{ display: "block", marginTop: 4, padding: "8px 12px",
                              background: "#FAF8F3", border: "1px solid rgba(27,35,64,0.15)",
                              borderRadius: 8, fontSize: 14, minWidth: 260 }}>
              {venues.map(v => (
                <option key={v.location_id || v.venue_id} value={v.location_id || v.venue_id}>
                  {v.name || v.slug || v.location_id}
                </option>
              ))}
            </select>
          </label>
        )}

        {status && (
          <>
            <div style={{ background: "#fff", border: "1px solid rgba(27,35,64,0.08)",
                           borderRadius: 12, padding: "20px 24px", marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div style={{ fontSize: 22, fontFamily: "Cormorant Garamond, serif" }}>
                  {venue?.name || "Your venue"} — Setup {status.percent_complete}% complete
                </div>
                <Link to={`/owner/venues/${venueId}`} data-testid="setup-open-dashboard"
                       style={{ fontSize: 13, color: "#B8913A", textDecoration: "none" }}>
                  Continue to dashboard →
                </Link>
              </div>
              <div style={{ marginTop: 10, height: 6, background: "rgba(27,35,64,0.08)",
                             borderRadius: 999, overflow: "hidden" }}>
                <div data-testid="setup-progress-bar" style={{
                  width: `${status.percent_complete}%`, height: "100%",
                  background: status.percent_complete === 100 ? "#1B4634" : "#B8913A",
                  transition: "width 400ms ease",
                }} />
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: "rgba(27,35,64,0.65)" }}>
                {status.steps_complete} of {status.steps_total} steps · autosaves as you go
              </div>
            </div>

            <ol style={{ listStyle: "none", padding: 0, margin: 0 }} data-testid="setup-checklist">
              {status.steps.map((s) => (
                <ChecklistItem key={s.key} step={s} disabled={saving}
                                venueId={venueId}
                                onToggle={(ack) => toggleManual(s, ack)} />
              ))}
            </ol>
          </>
        )}
      </div>
    </AppShell>
  );
}

// Deep-link map: each derived step points to the editor that fixes it.
const DEEP_LINKS = {
  venue_profile:       (vid) => `/owner/venues/${vid}?tab=details`,
  address_verified:    (vid) => `/owner/venues/${vid}?tab=details#address`,
  venue_photos:        (vid) => `/owner/venues/${vid}?tab=photos`,
  workspaces_added:    (vid) => `/owner/venues/${vid}?tab=workspaces`,
  workspace_photos:    (vid) => `/owner/venues/${vid}?tab=workspaces`,
  amenities:           (vid) => `/owner/venues/${vid}?tab=amenities`,
  pricing:             (vid) => `/owner/venues/${vid}?tab=pricing`,
  availability:        (vid) => `/owner/venues/${vid}?tab=availability`,
  booking_method:      (vid) => `/owner/venues/${vid}?tab=booking`,
  deposits:            (vid) => `/owner/venues/${vid}?tab=booking#deposits`,
  cancellation_policy: (vid) => `/owner/venues/${vid}?tab=policies`,
  booking_buffers:     (vid) => `/owner/venues/${vid}?tab=booking#buffers`,
  stripe:              () => `/owner/onboarding?step=stripe`,
  preview:             (vid) => `/owner/venues/${vid}/preview`,
  publish:             (vid) => `/owner/venues/${vid}?tab=publish`,
};

function ChecklistItem({ step, disabled, onToggle, venueId }) {
  const link = DEEP_LINKS[step.key]?.(venueId) || null;
  const done = step.done;
  const isManual = step.kind === "manual";
  const isNA = step.not_applicable;

  const dotBg = isNA ? "#DADCE0" : done ? "#1B4634" : "#fff";
  const dotBorder = done || isNA ? dotBg : "rgba(27,35,64,0.35)";
  const labelColor = isNA ? "rgba(27,35,64,0.5)" : done ? "rgba(27,35,64,0.55)" : "#1B2340";

  return (
    <li data-testid={`setup-step-${step.key}`}
         style={{ background: "#fff", padding: "14px 18px", marginBottom: 8,
                  border: "1px solid rgba(27,35,64,0.08)", borderRadius: 10,
                  display: "flex", alignItems: "center", gap: 14 }}>
      <button type="button" onClick={() => isManual && !disabled && onToggle(!done)}
               disabled={!isManual || disabled}
               data-testid={`setup-check-${step.key}`}
               aria-label={step.label}
               style={{
                 width: 22, height: 22, borderRadius: 4, background: dotBg,
                 border: `2px solid ${dotBorder}`, cursor: isManual ? "pointer" : "default",
                 flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                 padding: 0,
               }}>
        {done && <span style={{ color: "#FAF8F3", fontSize: 14, lineHeight: 1 }}>✓</span>}
      </button>
      <div style={{ flex: 1, textDecoration: done ? "line-through" : "none", color: labelColor }}>
        {step.label}
        {isNA && <span style={{ marginLeft: 10, fontSize: 11, color: "rgba(27,35,64,0.5)" }}>
          (not applicable — external booking only)
        </span>}
        {isManual && !done && <span style={{ marginLeft: 10, fontSize: 11, color: "#B8913A" }}>
          confirmation required
        </span>}
      </div>
      {link && !isNA && (
        <Link to={link} data-testid={`setup-link-${step.key}`}
               style={{ fontSize: 12, color: "#B8913A", textDecoration: "none",
                        fontWeight: 600, whiteSpace: "nowrap" }}>
          {done ? "Review →" : "Open →"}
        </Link>
      )}
    </li>
  );
}
